import type { CognitoRefusalCode, CognitoUnavailableCode, SessionOutcome } from './types';

const OAUTH_SCOPE = 'phone openid profile';

export function buildLoginUrl(params: {
  hostedUiUrl: string;
  clientId: string;
  redirectUri: string;
  state: string;
  codeChallenge: string;
}): string {
  const query = new URLSearchParams({
    client_id: params.clientId,
    response_type: 'code',
    redirect_uri: params.redirectUri,
    state: params.state,
    code_challenge: params.codeChallenge,
    code_challenge_method: 'S256',
    scope: OAUTH_SCOPE,
  });
  return `${params.hostedUiUrl}/login?${query.toString()}`;
}

export function buildLogoutUrl(params: { hostedUiUrl: string; clientId: string; logoutUri: string }): string {
  const query = new URLSearchParams({
    client_id: params.clientId,
    logout_uri: params.logoutUri,
  });
  return `${params.hostedUiUrl}/logout?${query.toString()}`;
}

interface StatePayload {
  p: string;
  n: string;
}

export function encodeState(params: { path: string; nonce: string }): string {
  const payload: StatePayload = { p: params.path, n: params.nonce };
  const bytes = new TextEncoder().encode(JSON.stringify(payload));
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
  return btoa(binary);
}

const STATE_PATH_ALLOWLIST = /^\/[A-Za-z0-9._~-]/;

export function decodeStatePath(state: string): string {
  const payload = tryDecodeState(state);
  if (payload && typeof payload.p === 'string' && STATE_PATH_ALLOWLIST.test(payload.p)) {
    return payload.p;
  }
  return '/';
}

export function decodeStateNonce(state: string): string {
  const payload = tryDecodeState(state);
  return payload && typeof payload.n === 'string' ? payload.n : '';
}

function tryDecodeState(state: string): Partial<StatePayload> | null {
  try {
    const bytes = Uint8Array.from(atob(state), (char) => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Partial<StatePayload>;
  } catch {
    return null;
  }
}

const TERMINAL_COGNITO_CODES = new Set<string>([
  'access_denied',
  'invalid_request',
  'unauthorized_client',
  'invalid_scope',
  'unsupported_response_type',
]);

const UNAVAILABLE_COGNITO_CODES = new Set<string>(['server_error', 'temporarily_unavailable']);

export function mapHostedUiError(error: string): SessionOutcome {
  if (TERMINAL_COGNITO_CODES.has(error)) {
    return { kind: 'terminal', source: 'cognito', code: error as CognitoRefusalCode };
  }
  if (UNAVAILABLE_COGNITO_CODES.has(error)) {
    return { kind: 'unavailable', source: 'cognito', code: error as CognitoUnavailableCode };
  }
  return { kind: 'unavailable', source: 'transport', code: 'network' };
}
